/**
 * Compose section of the bug-report panel — header (mode • version • branch),
 * the textarea + mic dictation, sync options, and the submit button. Lifted
 * out of `BugReportButton.tsx` so the parent stays under the file-size
 * hygiene threshold; pure props in / events out, no state of its own.
 *
 * The "branch: <name>" chip in the header comes from
 * `APP_GIT_BRANCH` injected at build time via `vite.config.ts`
 * (see `docs/runbooks/dev_sync_and_fix.md` §7).
 */
import { APP_GIT_BRANCH, APP_VERSION, isDefaultBranch } from "@/config/version";
import { BugReportMicButton } from "@/components/common/BugReportMicButton";
import { BugReportSyncOptions } from "@/components/common/BugReportSyncOptions";

export type BugReportComposeBodyProps = {
  mode: string;
  description: string;
  onDescriptionChange: (value: string) => void;
  onDictationAppend: (chunk: string) => void;
  submitting: boolean;
  submitError: string | null;
  submitDone: boolean;
  /** When submitDone, distinguishes IDE sync queue vs normal bug_reports. */
  submitDoneDevSync: boolean;
  canQueueSync: boolean;
  isOwnerSync: boolean;
  queueForDevSync: boolean;
  setQueueForDevSync: (value: boolean) => void;
  repoPath: string;
  setRepoPath: (value: string) => void;
  setGraphicFile: (file: File | null) => void;
  onSubmit: () => void;
};

function ComposeHeader({ mode }: { mode: string }) {
  const branchTag = APP_GIT_BRANCH && APP_GIT_BRANCH !== "unknown" ? APP_GIT_BRANCH : null;
  return (
    <div className="text-xs text-gray-500">
      Mode: <span className="font-medium text-gray-700">{mode}</span>
      {" • "}v{APP_VERSION}
      {branchTag ? (
        <>
          {" • "}
          <span
            className={isDefaultBranch(branchTag) ? "text-gray-500" : "font-medium text-amber-700"}
            title="Build-time git branch"
          >
            branch: {branchTag}
          </span>
        </>
      ) : null}
    </div>
  );
}

export function BugReportComposeBody(props: BugReportComposeBodyProps) {
  if (props.submitDone) {
    return (
      <div className="px-4 py-3 space-y-2">
        <ComposeHeader mode={props.mode} />
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 font-medium">
          {props.submitDoneDevSync
            ? "Queued for IDE sync — thanks!"
            : "Bug report filed — thanks!"}
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 py-3 space-y-2">
      <ComposeHeader mode={props.mode} />
      {/* Sync options above the textarea so mobile reporters see the queue
       * state without scrolling (dev-sync 89692b9f). */}
      {props.canQueueSync ? (
        <BugReportSyncOptions
          queueForDevSync={props.queueForDevSync}
          setQueueForDevSync={props.setQueueForDevSync}
          repoPath={props.repoPath}
          setRepoPath={props.setRepoPath}
          setGraphicFile={props.setGraphicFile}
          isOwnerSync={props.isOwnerSync}
          submitting={props.submitting}
        />
      ) : null}
      <textarea
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:opacity-50"
        rows={4}
        placeholder="Describe the bug — what happened, what you expected, what you were doing…"
        value={props.description}
        onChange={(e) => props.onDescriptionChange(e.target.value)}
        disabled={props.submitting}
        maxLength={4000}
      />
      {/*
       * Mic sits below the textarea (not inside it) so it never overlaps
       * typed content; available to every authenticated bug reporter, not
       * just users with dev-sync access. See dev-sync note
       * 1d78617e-731b-4eab-ab27-a0eb957b69fe.
       */}
      <div className="flex justify-end">
        <BugReportMicButton onAppend={props.onDictationAppend} disabled={props.submitting} />
      </div>
      {props.submitError ? <div className="text-xs text-red-600">{props.submitError}</div> : null}
      <button
        type="button"
        className="w-full rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-rose-700"
        onClick={() => props.onSubmit()}
        disabled={props.submitting || !props.description.trim()}
      >
        {props.submitting
          ? "Submitting…"
          : props.canQueueSync && (props.isOwnerSync || props.queueForDevSync)
            ? "Queue for IDE sync"
            : "Submit report"}
      </button>
    </div>
  );
}
